// Enhanced Card Components with Liquid Glass Effects
// Premium card components with multiple variants for anime content

import * as React from 'react';
import {
  Card as MuiCard,
  CardMedia as MuiCardMedia,
  CardContent as MuiCardContent,
  CardActions as MuiCardActions,
  CardHeader as MuiCardHeader,
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  styled,
  Theme,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { BookmarkBorder, Bookmark, StarBorder, Star, PlayCircle } from '@mui/icons-material';
import { designTokens } from '../../theme/tokens';

// Types
export interface AnimeCardProps {
  id: number;
  title: string;
  poster: string;
  score: number;
  type: string;
  episodes: number | '?' | string;
  genres?: Array<{ name: string } | string>;
  year?: number;
  status?: string;
  isAdult?: boolean;
  badge?: string; // 'New', '#1', 'Trending', etc.
  variant?: 'default' | 'large' | 'horizontal' | 'compact' | 'hero' | 'glass' | 'glass-elevated';
  onClick?: (id: number) => void;
  onBookmark?: (id: number, bookmarked: boolean) => void;
  onFavorite?: (id: number, favorited: boolean) => void;
  bookmarked?: boolean;
  favorited?: boolean;
  showActions?: boolean;
  className?: string;
  'data-testid'?: string;
}

// Enhanced Card Component with Liquid Glass Effects
export const AnimeCard = React.forwardRef<HTMLDivElement, AnimeCardProps>(
  (
    {
      id,
      title,
      poster,
      score,
      type,
      episodes,
      genres,
      year,
      status,
      isAdult,
      badge,
      variant = 'glass',
      onClick,
      onBookmark,
      onFavorite,
      bookmarked = false,
      favorited = false,
      showActions = true,
      className,
      'data-testid': testId,
      ...props
    },
    ref
  ) => {
    const [imageError, setImageError] = React.useState(false);
    const [hovered, setHovered] = React.useState(false);
    const [floatOffset, setFloatOffset] = React.useState(0);

    // Floating animation effect
    React.useEffect(() => {
      const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Calculate tilt based on mouse position
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const rotateX = ((y - centerY) / centerY) * 5; // 5 degrees max
        const rotateY = ((centerX - x) / centerX) * 5; // 5 degrees max

        // Apply 3D transform
        (e.currentTarget as HTMLDivElement).style.transform = `
          perspective(1000px)
          rotateX(${-rotateX}deg)
          rotateY(${rotateY}deg)
          translateY(${floatOffset}px)
        `;
      };

      const handleMouseLeave = () => {
        (ref.current as HTMLDivElement).style.transform = `
          perspective(1000px)
          rotateX(0deg)
          rotateY(0deg)
          translateY(${floatOffset}px)
        `;
      };

      ref.current?.addEventListener('mousemove', handleMouseMove);
      ref.current?.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        ref.current?.removeEventListener('mousemove', handleMouseMove);
        ref.current?.removeEventListener('mouseleave', handleMouseLeave);
      };
    }, [floatOffset, ref]);

    // Gentle floating animation
    React.useEffect(() => {
      const floatAnimation = () => {
        setFloatOffset(Math.sin(Date.now() * 0.002) * 4);
        requestAnimationFrame(floatAnimation);
      };
      requestAnimationFrame(floatAnimation);
    }, []);

    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onClick?.(id);
    };

    const handleBookmark = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onBookmark?.(id, !bookmarked);
    };

    const handleFavorite = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onFavorite?.(id, !favorited);
    };

    const formatEpisodes = () => {
      if (episodes === '?' || episodes === 'Unknown') return '? eps';
      if (typeof episodes === 'number') return `${episodes} eps`;
      return `${episodes} eps`;
    };

    const genreTags = genres?.slice(0, 2).map((g, i) => (
      <Chip
        key={i}
        label={typeof g === 'string' ? g : g.name}
        size="small"
        variant="outlined"
        sx={{
          fontSize: '0.65rem',
          height: 20,
          borderColor: 'rgba(255, 255, 255, 0.2)',
          color: 'white',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        }}
      />
    ));

    // Glass variant styles
    const getGlassStyle = () => {
      const isDark = true; // Assuming dark theme
      return {
        background: isDark
          ? designTokens.colors.glass.dark.medium
          : designTokens.colors.glass.light.medium,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: `1px solid ${isDark
          ? designTokens.colors.glass.dark.border.medium
          : designTokens.colors.glass.light.border.medium}`,
        boxShadow: isDark
          ? '0 8px 32px rgba(31, 38, 135, 0.37)'
          : '0 8px 32px rgba(31, 38, 135, 0.15)',
        '&:hover': {
          background: isDark
            ? designTokens.colors.glass.dark.strong
            : designTokens.colors.glass.light.strong,
          transform: 'translateY(-4px) scale(1.02)',
          boxShadow: isDark
            ? '0 12px 40px rgba(31, 38, 135, 0.45)'
            : '0 12px 40px rgba(31, 38, 135, 0.2)',
        }
      };
    };

    return (
      <motion.div
        ref={ref}
        data-testid={testId}
        className={className}
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.175, 0.885, 0.32, 1.275] }}
        style={{
          // Enable 3D transforms for parallax effect
          perspective: '1000px',
          transformStyle: 'preserve-3d',
        }}
      >
        <MuiCard
          variant={variant}
          sx={variant.startsWith('glass') ? getGlassStyle() : {}}
          onClick={onClick ? handleClick : undefined}
        >
          {/* Poster Image with Liquid Shine Effect */}
          <div
            style={{
              position: 'relative',
              aspectRatio: variant === 'horizontal' || variant === 'hero' ? '16 / 9' : '2 / 3',
              overflow: 'hidden'
            }}
          >
            {/* Shine Overlay for Liquid Effect */}
            {!imageError ? (
              <CardMedia
                component="img"
                src={poster}
                alt={title}
                loading="lazy"
                onError={() => setImageError(true)}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.8s cubic-bezier(0.25, 0.1, 0.25, 1.0)',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                  // Add subtle shine effect
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '50%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                    transform: 'skewX(-25deg)',
                    animation: 'shimmer 3s infinite',
                  }
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                }}
              >
                {/* Skeleton loader with pulse effect */}
                <Box sx={{
                  width: '100%',
                  height: '100%',
                  animation: 'pulse 2s infinite ease-in-out'
                }} />
              </Box>
            )}

            {/* Liquid Gradient Overlay */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(135deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 70%)',
                pointerEvents: 'none',
              }}
            />

            {/* Play Button Hover Effect */}
            {showActions && onClick && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 4,
                  right: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: 'rgba(139, 92, 246, 0.2)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  opacity: 0,
                  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  '&:hover': {
                    background: 'rgba(139, 92, 246, 0.4)',
                    transform: 'scale(1.1)',
                    boxShadow: '0 8px 25px rgba(139, 92, 246, 0.3)',
                  }
                }}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
              >
                {hovered && (
                  <>
                    <PlayCircle fontSize="large" color="white" />
                    {/* Ripple effect */}
                    <Box sx={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      animation: 'ripple 1.5s infinite',
                      opacity: 0.2,
                    }} />
                  </>
                )}
                {!hovered && (
                  <PlayCircle fontSize="large" color="white" />
                )}
              </Box>
            )}

            {/* Animated Badge System */}
            {badge && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  zIndex: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  background: 'rgba(139, 92, 246, 0.2)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  padding: '4px 10px',
                  borderRadius: 20,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: '#fff',
                  animation: 'float 3s ease-in-out infinite',
                }}
              >
                <Badge
                  color="error"
                  sx={{ fontSize: '0.75rem' }}
                >
                  {badge}
                </Badge>
                {/* Sparkle effect */}
                <Box sx={{
                  position: 'absolute',
                  right: -4,
                  top: -4,
                  width: 12,
                  height: 12,
                  background: 'radial-gradient(circle, #fff 0%, transparent 70%)',
                  opacity: 0.7,
                  animation: 'sparkle 2s ease-in-out infinite',
                }} />
              </Box>
            )}

            {/* Adult Badge */}
            {isAdult && (
              <Chip
                label="18+"
                size="small"
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 2,
                  fontSize: '0.65rem',
                  height: 20,
                  borderRadius: 4,
                  backgroundColor: '#F97316',
                  color: 'white',
                  fontWeight: 700,
                  backdropFilter: 'blur(4px)',
                  WebkitBackdropFilter: 'blur(4px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                }}
              />
            )}

            {/* Episode Badge with Pulse */}
            {variant !== 'horizontal' && variant !== 'hero' && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  left: 8,
                  zIndex: 2,
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: 4,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  backdropFilter: 'blur(4px)',
                  WebkitBackdropFilter: 'blur(4px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  animation: 'pulse 2s ease-in-out infinite',
                }}
              >
                {formatEpisodes()}
              </Box>
            )}

            {/* Status Badge with Gradient */}
            {status && variant === 'horizontal' && (
              <Chip
                label={status}
                size="small"
                variant="outlined"
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  zIndex: 2,
                  fontSize: '0.65rem',
                  height: 20,
                  borderColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  backdropFilter: 'blur(4px)',
                  WebkitBackdropFilter: 'blur(4px)',
                }}
              />
            )}
          </div>

          {/* Content Area with Glass Effect */}
          <Box
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              position: 'relative',
              zIndex: 2,
              background: variant.startsWith('glass')
                ? 'rgba(0,0,0,0.2)'
                : 'transparent',
              backdropFilter: variant.startsWith('glass')
                ? 'blur(4px)'
                : 'none',
              WebkitBackdropFilter: variant.startsWith('glass')
                ? 'blur(4px)'
                : 'none',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: variant === 'compact' ? '0.875rem' : variant === 'large' ? '1.125rem' : '1rem',
                lineHeight: 1.3,
                display: '-webkit-box',
                WebkitLineClamp: variant === 'horizontal' ? 1 : 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                color: '#fff',
                textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                letterSpacing: '-0.25px',
              }}
            >
              {title}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8' }}>
                {type}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8' }}>•</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8' }}>
                {formatEpisodes()}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#FBBF24', fontWeight: 600 }}>
                ★ {typeof score === 'number' ? score.toFixed(1) : score}
              </Typography>
              {year && (
                <>
                  <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8' }}>•</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8' }}>
                    {year}
                  </Typography>
                </>
              )}
            </Box>

            {genres && genres.length > 0 && variant !== 'compact' && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, marginTop: 1 }}>
                {genreTags}
              </Box>
            )}

            {/* Action Buttons with Hover Effects */}
            {showActions && (onBookmark || onFavorite) && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  marginTop: 2,
                  paddingTop: variant === 'horizontal' ? 0 : 2,
                  borderTop: variant === 'horizontal' ? '1px solid rgba(255,255,255,0.05)' : 'none',
                  justifyContent: variant === 'horizontal' ? 'flex-end' : 'flex-start',
                }}
              >
                {onBookmark && (
                  <Tooltip
                    title={bookmarked ? 'Remove from bookmarks' : 'Add to bookmarks'}
                    arrow
                  >
                    <IconButton
                      onClick={handleBookmark}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(0,0,0,0.3)',
                        color: 'white',
                        backdropFilter: 'blur(4px)',
                        WebkitBackdropFilter: 'blur(4px)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        '&:hover': {
                          bgcolor: 'rgba(139, 92, 246, 0.4)',
                          transform: 'scale(1.1)',
                          boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
                        }
                      }}
                      aria-label="Bookmark"
                    >
                      {bookmarked ? <Bookmark fontSize="small" /> : <BookmarkBorder fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                )}
                {onFavorite && (
                  <Tooltip
                    title={favorited ? 'Remove from favorites' : 'Add to favorites'}
                    arrow
                  >
                    <IconButton
                      onClick={handleFavorite}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(0,0,0,0.3)',
                        color: 'white',
                        backdropFilter: 'blur(4px)',
                        WebkitBackdropFilter: 'blur(4px)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        '&:hover': {
                          bgcolor: '#FBBF24',
                          color: '#0A0F1A',
                          transform: 'scale(1.1)',
                          boxShadow: '0 4px 12px rgba(251,191,36,0.3)',
                        }
                      }}
                      aria-label="Favorite"
                    >
                      {favorited ? <Star fontSize="small" sx={{ color: '#FBBF24' }} /> : <StarBorder fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
        </MuiCard>
      </motion.div>
    );
  }
);

AnimeCard.displayName = 'AnimeCard';

// Skeleton Card with Loading Animation
export const AnimeCardSkeleton = ({ variant = 'default', className }: { variant?: AnimeCardProps['variant']; className?: string }) => {
  const isHorizontal = variant === 'horizontal' || variant === 'hero';

  return (
    <div
      className={className}
      style={{
        minWidth: isHorizontal ? 320 : 160,
        maxWidth: isHorizontal ? '100%' : 220,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <div
        style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
          borderRadius: 24,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Shimmering Skeleton Image */}
        <div
          style={{
            width: '100%',
            height: isHorizontal ? '56.25%' : '60%', // 16:9 or 2:3 ratio
            background: 'linear-gradient(90deg, #1e293b 0%, #334155 50%, #1e293b 100%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 3s infinite linear',
            position: 'relative',
          }}
        >
          {/* Placeholder for play button */}
          <div
            style={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'rgba(139, 92, 246, 0.2)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
              border: '1px solid rgba(255,255,255,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'pulse 2s infinite ease-in-out',
            }}
          >
            <PlayCircle fontSize="small" color="white" opacity={0.7} />
          </div>
        </div>

        {/* Skeleton Content */}
        <div
          style={{
            padding: '16px',
            background: 'linear-gradient(to top, rgba(0,0,0,0.3) 0%, transparent 100%)',
          }}
        >
          <div
            style={{
              width: '70%',
              height: '18px',
              background: 'linear-gradient(90deg, #1e293b 0%, #334155 50%, #1e293b 100%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 2s infinite linear',
              borderRadius: 4,
              marginBottom: 8,
            }}
          />
          <div
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              marginBottom: 12,
            }}
          >
            <div
              style={{
                width: '40px',
                height: '12px',
                background: 'linear-gradient(90deg, #1e293b 0%, #334155 50%, #1e293b 100%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 2s infinite linear',
                borderRadius: 2,
              }}
            />
            <div
              style={{
                width: '60px',
                height: '12px',
                background: 'linear-gradient(90deg, #1e293b 0%, #334155 50%, #1e293b 100%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 2s infinite linear',
                borderRadius: 2,
              }}
            />
            <div
              style={{
                width: '80px',
                height: '18px',
                background: 'linear-gradient(90deg, #1e293b 0%, #334155 50%, #1e293b 100%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 2s infinite linear',
                borderRadius: 4,
                color: '#fff',
                fontSize: '12px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ★★
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              gap: '6px',
              flexWrap: 'wrap',
            }}
          >
            <div
              style={{
                width: '60px',
                height: '18px',
                background: 'linear-gradient(90deg, #1e293b 0%, #334155 50%, #1e293b 100%)',
                backgroundSize: '200% 100%',
                animation: 'shrimmer 3s infinite linear',
                borderRadius: 12,
                padding: '2px 6px',
                color: '#fff',
                fontSize: '10px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              Action
            </div>
            <div
              style={{
                width: '60px',
                height: '18px',
                background: 'linear-gradient(90deg, #1e293b 0%, #334155 50%, #1e293b 100%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 3s infinite linear',
                delay: '1s',
                borderRadius: 12,
                padding: '2px 6px',
                color: '#fff',
                fontSize: '10px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              Drama
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Generic Card Components
export const Card = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<typeof MuiCard> & { variant?: 'default' | 'elevated' | 'outlined' | 'glass' | 'glass-elevated' }>(
  ({ variant = 'default', children, className, ...props }, ref) => {
    return (
      <MuiCard
        ref={ref}
        variant="default"
        sx={variant.startsWith('glass')
          ? {
              background: 'rgba(17, 24, 39, 0.3)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              ':hover': {
                background: 'rgba(17, 24, 39, 0.4)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
              }
            }
          : {}}
        className={className}
        {...props}
      >
        {children}
      </MuiCard>
    );
  }
);

Card.displayName = 'Card';

export const CardContent = React.forwardRef<HTMLDivElement, React.ComponentPropsWithRef<typeof MuiCardContent>>(
  ({ children, className, ...props }, ref) => (
    <MuiCardContent
      ref={ref}
      className={className}
      sx={{
        padding: 2,
        background: 'linear-gradient(to top, rgba(0,0,0,0.2) 0%, transparent 100%)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
      }}
      {...props}
    >
      {children}
    </MuiCardContent>
  )
);

CardContent.displayName = 'CardContent';

export const CardActions = styled(MuiCardActions)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  gap: theme.spacing(1.5),
  flexWrap: 'wrap',
}));

export const CardHeader = styled(MuiCardHeader)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export default AnimeCard;